import {
  FileText,
  Plus,
  Bold,
  Italic,
  List as ListIcon,
  AlignLeft,
  Link2,
  FileDown,
  Send,
  ShieldCheck,
  LayoutTemplate,
  Database,
  Sparkles,
  History,
  Info,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ProposalScreenProps {
  active?: boolean;
  scenarioId: string;
  className?: string;
}

const PREFIX = 'bnk-proposal';
const NS = 'proposalScreen';

type StatusKey = 'draft' | 'review' | 'approved' | 'sent';

const STATUS_STYLE: Record<StatusKey, { fg: string; bg: string; border: string }> = {
  draft: { fg: '#7FB0FF', bg: 'rgba(45,126,255,0.14)', border: 'rgba(45,126,255,0.32)' },
  review: { fg: '#FBBF24', bg: 'rgba(251,191,36,0.14)', border: 'rgba(251,191,36,0.32)' },
  approved: { fg: '#34D399', bg: 'rgba(52,211,153,0.14)', border: 'rgba(52,211,153,0.32)' },
  sent: { fg: '#C4A2FF', bg: 'rgba(124,58,237,0.18)', border: 'rgba(124,58,237,0.36)' },
};

interface ProposalListItem {
  id: string;
  customer: string;
  subject: string;
  amount: string;
  date: string;
  status: StatusKey;
}

interface LineItem {
  pos: string;
  description: string;
  detail: string;
  quantity: string;
  unitPrice: string;
  total: string;
}

interface SourceItem {
  name: string;
  meta: string;
  relevance: string;
}

interface HistoryItem {
  time: string;
  event: string;
}

export function ProposalScreen({ active = true, scenarioId, className }: ProposalScreenProps) {
  const { t } = useTranslation('demo');
  // All visible content is read from the demo namespace scoped by scenarioId so
  // the component is generic over any proposal scenario.
  const k = (field: string) => `${NS}.${scenarioId}.${field}`;

  const list = t(k('list'), { returnObjects: true }) as ProposalListItem[];
  const lineItems = t(k('doc.lineItems'), { returnObjects: true }) as LineItem[];
  const tableHeaders = t(k('doc.tableHeaders'), { returnObjects: true }) as {
    position: string;
    description: string;
    quantity: string;
    unitPrice: string;
    total: string;
  };
  const sources = t(k('panel.sources'), { returnObjects: true }) as SourceItem[];
  const history = t(k('panel.history'), { returnObjects: true }) as HistoryItem[];

  // The selected proposal is the first/current one in the list (status reused for
  // the right-panel badge so list selection and metadata stay consistent).
  const selectedId = Array.isArray(list) && list.length > 0 ? list[0].id : '';

  const TOOLBAR_TOOLS: readonly { icon: typeof Bold; key: string }[] = [
    { icon: Bold, key: 'bold' },
    { icon: Italic, key: 'italic' },
    { icon: ListIcon, key: 'list' },
    { icon: AlignLeft, key: 'align' },
    { icon: Link2, key: 'link' },
  ];

  return (
    <div className={`${PREFIX}-root${active ? ` ${PREFIX}-active` : ''}${className ? ` ${className}` : ''}`}>
      <style>{css}</style>

      {/* ── Header ── */}
      <header className={`${PREFIX}-header`}>
        <span className={`${PREFIX}-icon-pill`} aria-hidden="true">
          <FileText size={20} color="#2D7EFF" strokeWidth={2.2} />
        </span>
        <div className={`${PREFIX}-header-text`}>
          <h1 className={`${PREFIX}-h1`}>{t(k('title'))}</h1>
          <p className={`${PREFIX}-subtitle`}>{t(k('subtitle'))}</p>
        </div>
      </header>

      {/* ── 3-region body ── */}
      <div className={`${PREFIX}-body`}>
        {/* LEFT rail — proposal list */}
        <aside className={`${PREFIX}-rail`} aria-label={t(k('listLabel'))}>
          <button type="button" className={`${PREFIX}-new`}>
            <Plus size={16} strokeWidth={2.4} aria-hidden="true" />
            {t(k('newProposal'))}
          </button>
          <ul className={`${PREFIX}-list`}>
            {(Array.isArray(list) ? list : []).map((item) => {
              const s = STATUS_STYLE[item.status] ?? STATUS_STYLE.draft;
              const selected = item.id === selectedId;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`${PREFIX}-list-item${selected ? ` ${PREFIX}-list-item--active` : ''}`}
                    aria-current={selected ? 'true' : undefined}
                  >
                    <div className={`${PREFIX}-list-top`}>
                      <span className={`${PREFIX}-list-id`}>{item.id}</span>
                      <span
                        className={`${PREFIX}-badge`}
                        style={{ color: s.fg, background: s.bg, borderColor: s.border }}
                      >
                        {t(k(`status.${item.status}`))}
                      </span>
                    </div>
                    <div className={`${PREFIX}-list-subject`} title={item.subject}>
                      {item.subject}
                    </div>
                    <div className={`${PREFIX}-list-meta`}>
                      <span className={`${PREFIX}-list-customer`} title={item.customer}>
                        {item.customer}
                      </span>
                      <span className={`${PREFIX}-list-amount`}>{item.amount}</span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* CENTER — toolbar + paper document */}
        <section className={`${PREFIX}-center`}>
          <div className={`${PREFIX}-toolbar`} role="toolbar" aria-label={t(k('toolbarLabel'))}>
            <div className={`${PREFIX}-tools`}>
              {TOOLBAR_TOOLS.map(({ icon: Icon, key }) => (
                <button type="button" key={key} className={`${PREFIX}-tool`} tabIndex={-1} aria-hidden="true">
                  <Icon size={15} strokeWidth={2} />
                </button>
              ))}
            </div>
            <div className={`${PREFIX}-toolbar-actions`}>
              <button type="button" className={`${PREFIX}-btn ${PREFIX}-btn--ghost`}>
                <FileDown size={15} strokeWidth={2} aria-hidden="true" />
                {t(k('exportPdf'))}
              </button>
              <button type="button" className={`${PREFIX}-btn ${PREFIX}-btn--primary`}>
                <Send size={15} strokeWidth={2} aria-hidden="true" />
                {t(k('send'))}
              </button>
            </div>
          </div>

          <div className={`${PREFIX}-paper-wrap`}>
            <article className={`${PREFIX}-paper`}>
              {/* sender header */}
              <div className={`${PREFIX}-doc-head`}>
                <div className={`${PREFIX}-doc-brand`}>
                  <span className={`${PREFIX}-doc-mark`} aria-hidden="true">
                    M
                  </span>
                  <div>
                    <div className={`${PREFIX}-doc-sender`}>{t(k('doc.senderName'))}</div>
                    <div className={`${PREFIX}-doc-tagline`}>{t(k('doc.senderTagline'))}</div>
                  </div>
                </div>
                <div className={`${PREFIX}-doc-senderinfo`}>
                  <div>{t(k('doc.senderAddress'))}</div>
                  <div>{t(k('doc.senderContact'))}</div>
                </div>
              </div>

              <div className={`${PREFIX}-doc-rule`} aria-hidden="true" />

              {/* recipient + meta */}
              <div className={`${PREFIX}-doc-parties`}>
                <div className={`${PREFIX}-doc-recipient`}>
                  <div className={`${PREFIX}-doc-reclabel`}>{t(k('doc.recipientLabel'))}</div>
                  <div className={`${PREFIX}-doc-recname`}>{t(k('doc.recipientName'))}</div>
                  <div className={`${PREFIX}-doc-recline`}>{t(k('doc.recipientContact'))}</div>
                  <div className={`${PREFIX}-doc-recline`}>{t(k('doc.recipientAddress'))}</div>
                </div>
                <dl className={`${PREFIX}-doc-meta`}>
                  <div className={`${PREFIX}-doc-metarow`}>
                    <dt>{t(k('doc.metaProposalNo'))}</dt>
                    <dd>{t(k('doc.metaProposalNoValue'))}</dd>
                  </div>
                  <div className={`${PREFIX}-doc-metarow`}>
                    <dt>{t(k('doc.metaDate'))}</dt>
                    <dd>{t(k('doc.metaDateValue'))}</dd>
                  </div>
                  <div className={`${PREFIX}-doc-metarow`}>
                    <dt>{t(k('doc.metaContact'))}</dt>
                    <dd>{t(k('doc.metaContactValue'))}</dd>
                  </div>
                </dl>
              </div>

              {/* subject */}
              <h2 className={`${PREFIX}-doc-subject`}>{t(k('doc.subject'))}</h2>

              {/* intro */}
              <p className={`${PREFIX}-doc-salutation`}>{t(k('doc.salutation'))}</p>
              <p className={`${PREFIX}-doc-intro`}>{t(k('doc.intro'))}</p>

              {/* line items */}
              <table className={`${PREFIX}-table`}>
                <thead>
                  <tr>
                    <th className={`${PREFIX}-th ${PREFIX}-th--pos`}>{tableHeaders.position}</th>
                    <th className={`${PREFIX}-th`}>{tableHeaders.description}</th>
                    <th className={`${PREFIX}-th ${PREFIX}-th--num`}>{tableHeaders.quantity}</th>
                    <th className={`${PREFIX}-th ${PREFIX}-th--num`}>{tableHeaders.unitPrice}</th>
                    <th className={`${PREFIX}-th ${PREFIX}-th--num`}>{tableHeaders.total}</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(lineItems) ? lineItems : []).map((li) => (
                    <tr key={li.pos} className={`${PREFIX}-tr`}>
                      <td className={`${PREFIX}-td ${PREFIX}-td--pos`}>{li.pos}</td>
                      <td className={`${PREFIX}-td`}>
                        <div className={`${PREFIX}-td-desc`}>{li.description}</div>
                        <div className={`${PREFIX}-td-detail`}>{li.detail}</div>
                      </td>
                      <td className={`${PREFIX}-td ${PREFIX}-td--num`}>{li.quantity}</td>
                      <td className={`${PREFIX}-td ${PREFIX}-td--num`}>{li.unitPrice}</td>
                      <td className={`${PREFIX}-td ${PREFIX}-td--num ${PREFIX}-td--strong`}>{li.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* totals */}
              <div className={`${PREFIX}-totals-wrap`}>
                <div className={`${PREFIX}-totals`}>
                  <div className={`${PREFIX}-totals-row`}>
                    <span>{t(k('doc.subtotalLabel'))}</span>
                    <span className={`${PREFIX}-totals-num`}>{t(k('doc.subtotalValue'))}</span>
                  </div>
                  <div className={`${PREFIX}-totals-row`}>
                    <span>{t(k('doc.vatLabel'))}</span>
                    <span className={`${PREFIX}-totals-num`}>{t(k('doc.vatValue'))}</span>
                  </div>
                  <div className={`${PREFIX}-totals-row ${PREFIX}-totals-row--grand`}>
                    <span>{t(k('doc.grandTotalLabel'))}</span>
                    <span className={`${PREFIX}-totals-num`}>{t(k('doc.grandTotalValue'))}</span>
                  </div>
                </div>
              </div>

              {/* closing + signature */}
              <p className={`${PREFIX}-doc-closing`}>{t(k('doc.closing'))}</p>
              <div className={`${PREFIX}-doc-sign`}>
                <div className={`${PREFIX}-doc-regards`}>{t(k('doc.regardsLine'))}</div>
                <div className={`${PREFIX}-doc-signname`}>{t(k('doc.signatureName'))}</div>
                <div className={`${PREFIX}-doc-signrole`}>{t(k('doc.signatureRole'))}</div>
              </div>

              {/* terms */}
              <div className={`${PREFIX}-doc-terms`}>
                <div className={`${PREFIX}-doc-terms-title`}>{t(k('doc.termsTitle'))}</div>
                <ul className={`${PREFIX}-doc-terms-list`}>
                  <li>{t(k('doc.termsValidity'))}</li>
                  <li>{t(k('doc.termsPayment'))}</li>
                  <li>{t(k('doc.termsDelivery'))}</li>
                  <li>{t(k('doc.termsWarranty'))}</li>
                </ul>
              </div>
            </article>
          </div>
        </section>

        {/* RIGHT — metadata + sources panel */}
        <aside className={`${PREFIX}-panel`} aria-label={t(k('panel.title'))}>
          <div className={`${PREFIX}-panel-scroll`}>
            <h2 className={`${PREFIX}-panel-title`}>{t(k('panel.title'))}</h2>

            {/* status */}
            <div className={`${PREFIX}-panel-block`}>
              <div className={`${PREFIX}-panel-label`}>{t(k('panel.statusLabel'))}</div>
              <span
                className={`${PREFIX}-badge ${PREFIX}-badge--lg`}
                style={{
                  color: STATUS_STYLE.approved.fg,
                  background: STATUS_STYLE.approved.bg,
                  borderColor: STATUS_STYLE.approved.border,
                }}
              >
                <ShieldCheck size={13} strokeWidth={2.2} aria-hidden="true" />
                {t(k('panel.statusValue'))}
              </span>
              <div className={`${PREFIX}-panel-sub`}>{t(k('panel.approvedBy'))}</div>
            </div>

            {/* template */}
            <div className={`${PREFIX}-panel-block`}>
              <div className={`${PREFIX}-panel-label`}>{t(k('panel.templateLabel'))}</div>
              <div className={`${PREFIX}-template`}>
                <span className={`${PREFIX}-template-icon`} aria-hidden="true">
                  <LayoutTemplate size={16} color="#2D7EFF" strokeWidth={2} />
                </span>
                <div>
                  <div className={`${PREFIX}-template-name`}>{t(k('panel.templateValue'))}</div>
                  <div className={`${PREFIX}-template-meta`}>{t(k('panel.templateMeta'))}</div>
                </div>
              </div>
            </div>

            {/* sources */}
            <div className={`${PREFIX}-panel-block`}>
              <div className={`${PREFIX}-panel-label`}>
                <Database size={13} strokeWidth={2} aria-hidden="true" />
                {t(k('panel.sourcesLabel'))}
              </div>
              <ul className={`${PREFIX}-sources`}>
                {(Array.isArray(sources) ? sources : []).map((src) => {
                  const pct = parseInt(src.relevance, 10);
                  const width = Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : 0;
                  return (
                    <li key={src.name} className={`${PREFIX}-source`}>
                      <div className={`${PREFIX}-source-top`}>
                        <FileText size={14} color="#94A3C2" strokeWidth={2} aria-hidden="true" />
                        <span className={`${PREFIX}-source-name`} title={src.name}>
                          {src.name}
                        </span>
                        <span className={`${PREFIX}-source-pct`}>{src.relevance}</span>
                      </div>
                      <div className={`${PREFIX}-source-meta`}>{src.meta}</div>
                      <div
                        className={`${PREFIX}-source-bar`}
                        role="img"
                        aria-label={`${t(k('panel.relevanceLabel'))} ${src.relevance}`}
                      >
                        <span className={`${PREFIX}-source-bar-fill`} style={{ width: `${width}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* auto-generated note */}
            <div className={`${PREFIX}-note`}>
              <Sparkles size={15} color="#00D4FF" strokeWidth={2} aria-hidden="true" />
              <div>
                <div className={`${PREFIX}-note-main`}>{t(k('panel.note'))}</div>
                <div className={`${PREFIX}-note-detail`}>
                  <Info size={11} strokeWidth={2.2} aria-hidden="true" />
                  {t(k('panel.noteDetail'))}
                </div>
              </div>
            </div>

            {/* history */}
            <div className={`${PREFIX}-panel-block`}>
              <div className={`${PREFIX}-panel-label`}>
                <History size={13} strokeWidth={2} aria-hidden="true" />
                {t(k('panel.historyLabel'))}
              </div>
              <ol className={`${PREFIX}-history`}>
                {(Array.isArray(history) ? history : []).map((h, i) => (
                  <li key={h.time} className={`${PREFIX}-history-item`}>
                    <span
                      className={`${PREFIX}-history-dot${i === 0 ? ` ${PREFIX}-history-dot--active` : ''}`}
                      aria-hidden="true"
                    />
                    <div>
                      <div className={`${PREFIX}-history-event`}>{h.event}</div>
                      <div className={`${PREFIX}-history-time`}>{h.time}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default ProposalScreen;

const css = `
.${PREFIX}-root { font-family: Inter, system-ui, sans-serif; color: #E2E8F0; display: flex; flex-direction: column; height: 100%; min-height: 0; }
.${PREFIX}-root * { box-sizing: border-box; }

/* entrance animation gated on .${PREFIX}-active; finished look when inactive */
.${PREFIX}-header, .${PREFIX}-body { opacity: 1; transform: none; }
.${PREFIX}-active .${PREFIX}-header { animation: ${PREFIX}-fade .5s ease both; }
.${PREFIX}-active .${PREFIX}-body { animation: ${PREFIX}-rise .55s cubic-bezier(.2,.7,.2,1) .06s both; }
@keyframes ${PREFIX}-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes ${PREFIX}-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

.${PREFIX}-header { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex: 0 0 auto; }
.${PREFIX}-icon-pill { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 11px; background: rgba(45,126,255,0.10); border: 1px solid rgba(45,126,255,0.22); flex-shrink: 0; }
.${PREFIX}-header-text { min-width: 0; }
.${PREFIX}-h1 { margin: 0; font-size: 21px; font-weight: 700; color: #E2E8F0; letter-spacing: -0.01em; }
.${PREFIX}-subtitle { margin: 2px 0 0; font-size: 13px; color: #94A3C2; }

.${PREFIX}-body { display: grid; grid-template-columns: 210px minmax(0, 1fr) 290px; gap: 14px; flex: 1 1 auto; min-height: 0; }

/* ── LEFT rail ── */
.${PREFIX}-rail { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.${PREFIX}-new { display: inline-flex; align-items: center; justify-content: center; gap: 8px; width: 100%; height: 40px; border: none; border-radius: 11px; background: #2D7EFF; color: #fff; font-family: inherit; font-size: 13.5px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 14px rgba(45,126,255,0.30); transition: background .15s; flex: 0 0 auto; }
.${PREFIX}-new:hover { background: #1F6BEF; }
.${PREFIX}-list { list-style: none; margin: 0; padding: 0 2px 2px 0; display: flex; flex-direction: column; gap: 8px; overflow-y: auto; flex: 1 1 auto; min-height: 0; scrollbar-width: none; }
.${PREFIX}-list::-webkit-scrollbar { width: 0; height: 0; }
.${PREFIX}-list-item { display: block; width: 100%; text-align: left; padding: 11px 12px; border-radius: 12px; background: #141B27; border: 1px solid #1E2D45; color: inherit; font-family: inherit; cursor: pointer; transition: border-color .15s, background .15s, transform .15s; }
.${PREFIX}-list-item:hover { background: rgba(255,255,255,0.04); border-color: #30415F; transform: translateY(-1px); }
.${PREFIX}-list-item--active { background: rgba(45,126,255,0.10); border-color: rgba(45,126,255,0.45); box-shadow: inset 3px 0 0 #2D7EFF; }
.${PREFIX}-list-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 7px; }
.${PREFIX}-list-id { font-size: 11.5px; font-weight: 600; color: #94A3C2; font-variant-numeric: tabular-nums; letter-spacing: .01em; }
.${PREFIX}-list-subject { font-size: 13.5px; font-weight: 600; color: #E2E8F0; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.${PREFIX}-list-meta { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; margin-top: 6px; }
.${PREFIX}-list-customer { font-size: 12px; color: #94A3C2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.${PREFIX}-list-amount { font-size: 12px; font-weight: 600; color: #E2E8F0; font-variant-numeric: tabular-nums; white-space: nowrap; flex-shrink: 0; }

.${PREFIX}-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 999px; border: 1px solid transparent; font-size: 11px; font-weight: 600; white-space: nowrap; }
.${PREFIX}-badge--lg { font-size: 12.5px; padding: 5px 12px; }

/* ── CENTER ── */
.${PREFIX}-center { display: flex; flex-direction: column; gap: 12px; min-width: 0; min-height: 0; }
.${PREFIX}-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 7px 10px; background: #141B27; border: 1px solid #1E2D45; border-radius: 12px; flex: 0 0 auto; flex-wrap: wrap; }
.${PREFIX}-tools { display: flex; align-items: center; gap: 2px; }
.${PREFIX}-tool { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 8px; border: none; background: transparent; color: #94A3C2; cursor: default; }
.${PREFIX}-tool:hover { background: rgba(255,255,255,0.05); color: #E2E8F0; }
.${PREFIX}-toolbar-actions { display: flex; align-items: center; gap: 8px; }
.${PREFIX}-btn { display: inline-flex; align-items: center; gap: 7px; height: 34px; padding: 0 14px; border-radius: 9px; font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap; transition: background .15s, border-color .15s; }
.${PREFIX}-btn--ghost { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: #94A3C2; }
.${PREFIX}-btn--ghost:hover { background: rgba(255,255,255,0.08); color: #E2E8F0; border-color: #30415F; }
.${PREFIX}-btn--primary { background: #2D7EFF; border: 1px solid #2D7EFF; color: #fff; box-shadow: 0 4px 14px rgba(45,126,255,0.30); }
.${PREFIX}-btn--primary:hover { background: #1F6BEF; border-color: #1F6BEF; }

.${PREFIX}-paper-wrap { flex: 1 1 auto; min-height: 0; overflow-y: auto; border-radius: 14px; background: #0A0F1C; border: 1px solid #1E2D45; padding: 20px; scrollbar-width: none; }
.${PREFIX}-paper-wrap::-webkit-scrollbar { width: 0; height: 0; }

/* ── light "paper" document inside the dark app ── */
.${PREFIX}-paper { max-width: 720px; margin: 0 auto; background: #F8FAFC; color: #1F2937; border-radius: 10px; padding: 38px 44px 34px; box-shadow: 0 20px 50px rgba(0,0,0,0.5), 0 2px 0 rgba(255,255,255,0.04); }

.${PREFIX}-doc-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px 18px; flex-wrap: wrap; }
.${PREFIX}-doc-brand { display: flex; align-items: center; gap: 11px; min-width: 0; flex: 0 1 auto; }
.${PREFIX}-doc-mark { display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; border-radius: 10px; background: linear-gradient(135deg, #2D7EFF, #00D4FF); color: #fff; font-size: 20px; font-weight: 800; letter-spacing: -0.02em; flex-shrink: 0; }
.${PREFIX}-doc-sender { font-size: 17px; font-weight: 800; color: #0F172A; letter-spacing: -0.01em; }
.${PREFIX}-doc-tagline { font-size: 11.5px; color: #64748B; margin-top: 1px; }
.${PREFIX}-doc-senderinfo { flex: 0 1 auto; text-align: right; font-size: 10.5px; line-height: 1.6; color: #64748B; max-width: 230px; }

.${PREFIX}-doc-rule { height: 2px; background: linear-gradient(90deg, #2D7EFF, #00D4FF 60%, transparent); border-radius: 2px; margin: 16px 0 20px; }

.${PREFIX}-doc-parties { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px 24px; flex-wrap: wrap; margin-bottom: 22px; }
.${PREFIX}-doc-recipient { min-width: 0; flex: 1 1 220px; }
.${PREFIX}-doc-reclabel { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #94A3B8; margin-bottom: 5px; }
.${PREFIX}-doc-recname { font-size: 15px; font-weight: 700; color: #0F172A; }
.${PREFIX}-doc-recline { font-size: 12.5px; color: #475569; line-height: 1.55; }
.${PREFIX}-doc-meta { margin: 0; display: flex; flex-direction: column; gap: 5px; flex: 0 1 auto; min-width: 200px; }
.${PREFIX}-doc-metarow { display: flex; align-items: baseline; justify-content: space-between; gap: 14px; font-size: 12px; }
.${PREFIX}-doc-metarow dt { color: #94A3B8; }
.${PREFIX}-doc-metarow dd { margin: 0; font-weight: 600; color: #1E293B; font-variant-numeric: tabular-nums; }

.${PREFIX}-doc-subject { margin: 0 0 16px; font-size: 16px; font-weight: 800; color: #0F172A; letter-spacing: -0.01em; }
.${PREFIX}-doc-salutation { margin: 0 0 8px; font-size: 13px; color: #1F2937; }
.${PREFIX}-doc-intro { margin: 0 0 22px; font-size: 13px; line-height: 1.65; color: #334155; }

/* line-items table */
.${PREFIX}-table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
.${PREFIX}-th { text-align: left; padding: 9px 10px; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #fff; background: #1E293B; }
.${PREFIX}-th:first-child { border-top-left-radius: 7px; }
.${PREFIX}-th:last-child { border-top-right-radius: 7px; }
.${PREFIX}-th--pos { width: 36px; text-align: center; }
.${PREFIX}-th--num { text-align: right; white-space: nowrap; }
.${PREFIX}-tr { border-bottom: 1px solid #E2E8F0; }
.${PREFIX}-tr:nth-child(even) { background: #F1F5F9; }
.${PREFIX}-td { padding: 9px 10px; font-size: 12.5px; color: #334155; vertical-align: top; }
.${PREFIX}-td--pos { text-align: center; color: #94A3B8; font-weight: 600; font-variant-numeric: tabular-nums; }
.${PREFIX}-td--num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
.${PREFIX}-td--strong { font-weight: 700; color: #0F172A; }
.${PREFIX}-td-desc { font-weight: 600; color: #1E293B; }
.${PREFIX}-td-detail { font-size: 11px; color: #94A3B8; margin-top: 2px; line-height: 1.45; }

/* totals */
.${PREFIX}-totals-wrap { display: flex; justify-content: flex-end; margin-bottom: 22px; }
.${PREFIX}-totals { width: 300px; max-width: 100%; }
.${PREFIX}-totals-row { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; padding: 6px 10px; font-size: 12.5px; color: #475569; }
.${PREFIX}-totals-num { font-variant-numeric: tabular-nums; font-weight: 600; color: #1E293B; }
.${PREFIX}-totals-row--grand { margin-top: 4px; padding: 11px 10px; background: rgba(45,126,255,0.08); border-radius: 8px; font-size: 14.5px; font-weight: 800; color: #0F172A; border: 1px solid rgba(45,126,255,0.25); }
.${PREFIX}-totals-row--grand .${PREFIX}-totals-num { font-weight: 800; color: #0F172A; }

.${PREFIX}-doc-closing { margin: 0 0 20px; font-size: 13px; line-height: 1.65; color: #334155; }
.${PREFIX}-doc-sign { margin-bottom: 22px; }
.${PREFIX}-doc-regards { font-size: 13px; color: #1F2937; margin-bottom: 14px; }
.${PREFIX}-doc-signname { font-size: 13.5px; font-weight: 700; color: #0F172A; }
.${PREFIX}-doc-signrole { font-size: 11.5px; color: #64748B; margin-top: 1px; }

.${PREFIX}-doc-terms { border-top: 1px solid #E2E8F0; padding-top: 16px; }
.${PREFIX}-doc-terms-title { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #94A3B8; margin-bottom: 9px; }
.${PREFIX}-doc-terms-list { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 7px 24px; }
.${PREFIX}-doc-terms-list li { position: relative; padding-left: 16px; font-size: 12px; color: #475569; line-height: 1.5; }
.${PREFIX}-doc-terms-list li::before { content: ''; position: absolute; left: 0; top: 6px; width: 6px; height: 6px; border-radius: 50%; background: #2D7EFF; }

/* ── RIGHT panel ── */
.${PREFIX}-panel { background: #141B27; border: 1px solid #1E2D45; border-radius: 14px; min-height: 0; display: flex; box-shadow: 0 1px 2px rgba(0,0,0,0.3); }
.${PREFIX}-panel-scroll { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 18px; scrollbar-width: none; }
.${PREFIX}-panel-scroll::-webkit-scrollbar { width: 0; height: 0; }
.${PREFIX}-panel-title { margin: 0 0 16px; font-size: 14px; font-weight: 700; color: #E2E8F0; }
.${PREFIX}-panel-block { padding-bottom: 16px; margin-bottom: 16px; border-bottom: 1px solid #1E2D45; }
.${PREFIX}-panel-block:last-child { border-bottom: none; padding-bottom: 0; margin-bottom: 0; }
.${PREFIX}-panel-label { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #4A5568; margin-bottom: 9px; }
.${PREFIX}-panel-sub { font-size: 11.5px; color: #94A3C2; margin-top: 8px; }

.${PREFIX}-template { display: flex; align-items: center; gap: 10px; padding: 11px 12px; border-radius: 10px; background: #0A0F1C; border: 1px solid #1E2D45; }
.${PREFIX}-template-icon { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 9px; background: rgba(45,126,255,0.12); border: 1px solid rgba(45,126,255,0.22); flex-shrink: 0; }
.${PREFIX}-template-name { font-size: 13px; font-weight: 600; color: #E2E8F0; }
.${PREFIX}-template-meta { font-size: 11.5px; color: #94A3C2; margin-top: 1px; }

.${PREFIX}-sources { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 11px; }
.${PREFIX}-source { padding: 11px 12px; border-radius: 10px; background: #0A0F1C; border: 1px solid #1E2D45; }
.${PREFIX}-source-top { display: flex; align-items: center; gap: 7px; }
.${PREFIX}-source-name { flex: 1; min-width: 0; font-size: 12.5px; font-weight: 600; color: #E2E8F0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.${PREFIX}-source-pct { font-size: 11.5px; font-weight: 700; color: #34D399; font-variant-numeric: tabular-nums; flex-shrink: 0; }
.${PREFIX}-source-meta { font-size: 11px; color: #94A3C2; margin: 5px 0 8px; line-height: 1.45; }
.${PREFIX}-source-bar { height: 5px; border-radius: 999px; background: #1A2334; overflow: hidden; }
.${PREFIX}-source-bar-fill { display: block; height: 100%; border-radius: 999px; background: linear-gradient(90deg, #2D7EFF, #00D4FF); }
.${PREFIX}-active .${PREFIX}-source-bar-fill { animation: ${PREFIX}-grow .7s cubic-bezier(.2,.7,.2,1) .25s both; }
@keyframes ${PREFIX}-grow { from { transform: scaleX(0); transform-origin: left; } to { transform: scaleX(1); transform-origin: left; } }

.${PREFIX}-note { display: flex; align-items: flex-start; gap: 10px; padding: 12px 13px; margin-bottom: 16px; border-radius: 11px; background: rgba(0,212,255,0.06); border: 1px solid rgba(0,212,255,0.22); }
.${PREFIX}-note-main { font-size: 12.5px; font-weight: 600; color: #E2E8F0; line-height: 1.4; }
.${PREFIX}-note-detail { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #94A3C2; margin-top: 4px; }

.${PREFIX}-history { list-style: none; margin: 0; padding: 0; position: relative; }
.${PREFIX}-history::before { content: ''; position: absolute; left: 4px; top: 6px; bottom: 6px; width: 1px; background: #1E2D45; }
.${PREFIX}-history-item { position: relative; padding: 0 0 13px 18px; }
.${PREFIX}-history-item:last-child { padding-bottom: 0; }
.${PREFIX}-history-dot { position: absolute; left: 0; top: 4px; width: 9px; height: 9px; border-radius: 50%; background: #30415F; border: 2px solid #141B27; }
.${PREFIX}-history-dot--active { background: #34D399; box-shadow: 0 0 0 3px rgba(52,211,153,0.18); }
.${PREFIX}-history-event { font-size: 12.5px; color: #E2E8F0; line-height: 1.4; }
.${PREFIX}-history-time { font-size: 11px; color: #4A5568; margin-top: 1px; font-variant-numeric: tabular-nums; }

/* responsive: collapse to single column for narrower app windows */
@media (max-width: 1080px) {
  .${PREFIX}-body { grid-template-columns: 220px minmax(0, 1fr); }
  .${PREFIX}-panel { display: none; }
}
/* phones: the rail + document stack vertically; the document is the hero.
   The line-items table can exceed the narrow paper, so let the paper scroll
   horizontally within its card instead of clipping the whole screen. */
@media (max-width: 640px) {
  .${PREFIX}-body { grid-template-columns: 1fr; gap: 12px; }
  .${PREFIX}-rail { max-height: 156px; }
  .${PREFIX}-paper-wrap { padding: 12px; overflow: auto; }
  .${PREFIX}-paper { padding: 22px 18px 20px; }
  .${PREFIX}-doc-terms-list { grid-template-columns: 1fr; gap: 7px; }
  .${PREFIX}-th, .${PREFIX}-td { padding: 7px 7px; font-size: 11px; }
}
@media (prefers-reduced-motion: reduce) {
  .${PREFIX}-active .${PREFIX}-header,
  .${PREFIX}-active .${PREFIX}-body,
  .${PREFIX}-active .${PREFIX}-source-bar-fill { animation: none; }
}

/* ── light-mode overrides (app chrome only; the document paper stays light) ── */
:root:not(.dark) .${PREFIX}-root,
:root:not(.dark) .${PREFIX}-h1,
:root:not(.dark) .${PREFIX}-list-subject,
:root:not(.dark) .${PREFIX}-list-amount,
:root:not(.dark) .${PREFIX}-panel-title,
:root:not(.dark) .${PREFIX}-template-name,
:root:not(.dark) .${PREFIX}-source-name,
:root:not(.dark) .${PREFIX}-note-main,
:root:not(.dark) .${PREFIX}-history-event { color: #0f172a; }
:root:not(.dark) .${PREFIX}-subtitle,
:root:not(.dark) .${PREFIX}-list-id,
:root:not(.dark) .${PREFIX}-list-customer,
:root:not(.dark) .${PREFIX}-tool,
:root:not(.dark) .${PREFIX}-panel-sub,
:root:not(.dark) .${PREFIX}-template-meta,
:root:not(.dark) .${PREFIX}-source-meta,
:root:not(.dark) .${PREFIX}-note-detail { color: #475569; }
:root:not(.dark) .${PREFIX}-panel-label,
:root:not(.dark) .${PREFIX}-history-time { color: #94a3b8; }
:root:not(.dark) .${PREFIX}-list-item,
:root:not(.dark) .${PREFIX}-toolbar,
:root:not(.dark) .${PREFIX}-panel { background: #f8fafc; border-color: #e2e8f0; }
:root:not(.dark) .${PREFIX}-template,
:root:not(.dark) .${PREFIX}-source { background: #eef2f7; border-color: #e2e8f0; }
:root:not(.dark) .${PREFIX}-paper-wrap { background: #eef2f7; border-color: #e2e8f0; }
:root:not(.dark) .${PREFIX}-source-bar { background: #e2e8f0; }
:root:not(.dark) .${PREFIX}-panel-block { border-bottom-color: #e2e8f0; }
:root:not(.dark) .${PREFIX}-history::before { background: #e2e8f0; }
:root:not(.dark) .${PREFIX}-history-dot { background: #cbd5e1; border-color: #f8fafc; }
:root:not(.dark) .${PREFIX}-list-item:hover { background: rgba(15,23,42,0.03); border-color: #cbd5e1; }
:root:not(.dark) .${PREFIX}-tool:hover { background: rgba(15,23,42,0.05); color: #0f172a; }
:root:not(.dark) .${PREFIX}-btn--ghost { background: rgba(15,23,42,0.04); border-color: rgba(15,23,42,0.08); }
:root:not(.dark) .${PREFIX}-btn--ghost:hover { background: rgba(15,23,42,0.08); color: #0f172a; border-color: #cbd5e1; }
:root:not(.dark) .${PREFIX}-list-item--active { background: rgba(45,126,255,0.08); border-color: rgba(45,126,255,0.45); }
`;
